import { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography, Sheet } from '@mui/joy';
import ProductManager from './ProductManager';
import CategoryManager from './CategoryManager';
import StatCard from './StatCard';
import { getProducts, getCategories } from '../../services/api';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const SECTIONS = [
  { id: 'overview', label: 'Обзор', icon: <DashboardIcon /> },
  { id: 'products', label: 'Товары', icon: <InventoryIcon /> },
  { id: 'categories', label: 'Категории', icon: <CategoryIcon /> },
];

function AdminDashboard({ onLogout }) {
  const [activeSection, setActiveSection] = useState('products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [prodData, catData] = await Promise.all([getProducts(), getCategories()]);
      setProducts(Array.isArray(prodData) ? prodData : []);
      setCategories(Array.isArray(catData) ? catData : []);
    } catch (e) {
      console.error(e);
    }
    setStatsLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <Box>
            <Typography level="h3" sx={{ mb: 2 }}>Обзор</Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
              mb: 3,
            }}>
              <StatCard
                title="Всего товаров"
                value={statsLoading ? '...' : products.length}
                icon={<InventoryIcon />}
                color="primary"
              />
              <StatCard
                title="Категорий"
                value={statsLoading ? '...' : categories.length}
                icon={<CategoryIcon />}
                color="success"
              />
              <StatCard
                title="Наценка по умолчанию"
                value="1.0"
                icon={<DashboardIcon />}
                color="warning"
              />
            </Box>
            <Sheet variant="outlined" sx={{ borderRadius: 16, p: 3 }}>
              <Typography level="title-md" sx={{ mb: 1 }}>Быстрые действия</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button onClick={() => setActiveSection('products')}>Управлять товарами</Button>
                <Button variant="outlined" color="neutral" onClick={() => setActiveSection('categories')}>Управлять категориями</Button>
              </Box>
            </Sheet>
          </Box>
        );
      case 'products':
        return <ProductManager categories={categories} onRefresh={fetchStats} />;
      case 'categories':
        return <CategoryManager onRefresh={fetchStats} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: 'background.body' }}>
      {/* Sidebar */}
      <Sheet
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          position: { md: 'sticky' },
          top: 0,
          left: 0,
          height: { md: '100vh' },
          width: { xs: '100%', md: 260 },
          minWidth: { md: 220 },
          flexShrink: 0,
          borderRight: { md: '1px solid' },
          borderBottom: { xs: '1px solid', md: 'none' },
          borderColor: 'divider',
          zIndex: 10,
          boxSizing: 'border-box',
        }}
      >
        <Box>
          <Box sx={{ px: 1, py: 0.5, mb: 2 }}>
            <Typography level="title-lg">NASCAR</Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Админ-панель</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {SECTIONS.map((section) => (
              <Button
                key={section.id}
                variant={activeSection === section.id ? 'soft' : 'plain'}
                color={activeSection === section.id ? 'primary' : 'neutral'}
                startDecorator={section.icon}
                onClick={() => setActiveSection(section.id)}
                sx={{ justifyContent: 'flex-start', width: '100%' }}
              >
                {section.label}
              </Button>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Button
            variant="plain"
            color="neutral"
            startDecorator={<OpenInNewIcon />}
            onClick={() => window.open('/', '_blank')}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            На сайт
          </Button>
          <Button
            variant="plain"
            color="danger"
            startDecorator={<LogoutIcon />}
            onClick={onLogout}
            sx={{ justifyContent: 'flex-start', width: '100%' }}
          >
            Выйти
          </Button>
        </Box>
      </Sheet>

      {/* Main content */}
      <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto', minWidth: 0 }}>
        {renderContent()}
      </Box>
    </Box>
  );
}

export default AdminDashboard;
